
import { type FilterOptions } from '@resolute/schema';
import { formatINRCompact } from '@resolute/utils';
import { X } from 'lucide-react';

import { type SearchState } from '../search-query';
import { type UseSearchState } from '../use-search-state';

interface Chip {
  key: string;
  label: string;
  onRemove: () => void;
}

interface ActiveFiltersProps
  extends Pick<UseSearchState, 'setFilters' | 'toggleArrayValue' | 'clear'> {
  state: SearchState;
  /** Resolves ids (city/area/developer/property type — all CPzone-sourced,
   *  see filter-options) to their display label. Falls back to the raw
   *  value while options are still loading, rather than showing nothing. */
  options?: FilterOptions;
}

function labelFor(list: FilterOptions['cities'] | undefined, value: string): string {
  return list?.find((o) => o.value === value)?.label ?? value;
}

function buildChips(
  state: SearchState,
  options: FilterOptions | undefined,
  setFilters: UseSearchState['setFilters'],
  toggleArrayValue: UseSearchState['toggleArrayValue'],
): Chip[] {
  const chips: Chip[] = [];

  if (state.q) {
    chips.push({
      key: 'q',
      label: `"${state.q}"`,
      onRemove: () => setFilters({ q: undefined }),
    });
  }
  if (state.category) {
    chips.push({
      key: 'category',
      label: labelFor(options?.categories, state.category),
      onRemove: () => setFilters({ category: undefined }),
    });
  }
  if (state.state) {
    chips.push({
      key: 'state',
      label: labelFor(options?.states, state.state),
      onRemove: () => setFilters({ state: undefined }),
    });
  }
  if (state.city) {
    chips.push({
      key: 'city',
      label: labelFor(options?.cities, state.city),
      onRemove: () => setFilters({ city: undefined }),
    });
  }
  state.areas.forEach((a) =>
    chips.push({
      key: `area-${a}`,
      label: labelFor(options?.areas, a),
      onRemove: () => toggleArrayValue('areas', a),
    }),
  );
  state.type.forEach((t) =>
    chips.push({
      key: `type-${t}`,
      label: labelFor(options?.propertyTypes, t),
      onRemove: () => toggleArrayValue('type', t),
    }),
  );
  if (state.developer) {
    chips.push({
      key: 'developer',
      label: labelFor(options?.developers, state.developer),
      onRemove: () => setFilters({ developer: undefined }),
    });
  }
  state.config.forEach((c) =>
    chips.push({ key: `config-${c}`, label: `${c} BHK`, onRemove: () => toggleArrayValue('config', c) }),
  );
  state.listingStatus.forEach((s) =>
    chips.push({
      key: `status-${s}`,
      label: labelFor(options?.listingStatuses, s),
      onRemove: () => toggleArrayValue('listingStatus', s),
    }),
  );
  state.amenities.forEach((a) =>
    chips.push({
      key: `amenity-${a}`,
      label: labelFor(options?.amenities, a),
      onRemove: () => toggleArrayValue('amenities', a),
    }),
  );

  if (state.priceMin != null || state.priceMax != null) {
    const min = state.priceMin != null ? formatINRCompact(state.priceMin) : 'Any';
    const max = state.priceMax != null ? formatINRCompact(state.priceMax) : 'Any';
    chips.push({
      key: 'price',
      label: `${min} – ${max}`,
      onRemove: () => setFilters({ priceMin: undefined, priceMax: undefined }),
    });
  }
  if (state.areaMin != null || state.areaMax != null) {
    const min = state.areaMin != null ? `${state.areaMin} sq.ft` : 'Any';
    const max = state.areaMax != null ? `${state.areaMax} sq.ft` : 'Any';
    chips.push({
      key: 'area-range',
      label: `${min} – ${max}`,
      onRemove: () => setFilters({ areaMin: undefined, areaMax: undefined }),
    });
  }
  if (state.possessionStart || state.possessionEnd) {
    chips.push({
      key: 'possession-range',
      label: `Possession: ${state.possessionStart ?? 'Any'} – ${state.possessionEnd ?? 'Any'}`,
      onRemove: () => setFilters({ possessionStart: undefined, possessionEnd: undefined }),
    });
  }
  if (state.verified) chips.push({ key: 'verified', label: 'Verified', onRemove: () => setFilters({ verified: false }) });
  if (state.preLeased)
    chips.push({ key: 'preleased', label: 'Pre-leased', onRemove: () => setFilters({ preLeased: false }) });
  if (state.giftCity)
    chips.push({ key: 'giftcity', label: 'GIFT City', onRemove: () => setFilters({ giftCity: false }) });

  return chips;
}

export function ActiveFilters({ state, options, setFilters, toggleArrayValue, clear }: ActiveFiltersProps) {
  const chips = buildChips(state, options, setFilters, toggleArrayValue);
  if (chips.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2" aria-label="Active filters">
      {chips.map((chip) => (
        <button
          key={chip.key}
          type="button"
          onClick={chip.onRemove}
          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-sm transition-colors duration-fast ease-out-expo hover:bg-muted"
          aria-label={`Remove filter ${chip.label}`}
        >
          {chip.label}
          <X className="size-3.5 text-muted-foreground" aria-hidden="true" />
        </button>
      ))}
      {chips.length > 1 && (
        <button
          type="button"
          onClick={clear}
          className="ml-1 text-sm font-medium text-primary hover:underline"
        >
          Clear all
        </button>
      )}
    </div>
  );
}
